import { inScope, roleFitsUser, scopeOf } from './org-scope';
/**
 * 权限清单（CR-038）。
 *
 * 角色不再是枚举，而是 `Role` 表里的一行：名字 + 一组勾上的权限。
 * **代码里写死的是「有哪些权限」，不是「谁有」** —— 谁有什么由超管在角色页勾，
 * 这份清单只负责给勾选框编号，以及让前后端对同一个字符串达成一致。
 *
 * 两个互相独立的轴：
 * - **能进哪些工地**：`site.viewAll`（所有工地）vs 没有（只进 `SiteMember` 指派的工地）。
 *   这是原来 `canSeeAllSites` 的位置，原样保留，只是变成一个勾。
 * - **在能进的工地里能干什么**：每个模块拆「填写」「查看」。
 *   **查看 = 看本工地所有人的记录**；填写者不勾查看也永远能看自己填的。
 *
 * 两个轴不互相推导。勾了 `site.viewAll` 不会自动获得各模块的「查看」，
 * 反过来也一样 —— 想让一个角色「所有工地 + 所有人的记录」，两类勾都打上。
 * 那是配置，不是代码里的隐含规则（客户起初认为前者推出后者，讨论后接受独立，见 CR-038）。
 *
 * 新加一个权限（CR-043 时整理的配方，每一步都有测试挡着）：
 * 1. 这里加 key，`PERMISSION_GROUPS` 里归组（`permissions.test`）；有依赖就进 `PERMISSION_IMPLIES`；
 * 2. `api-permissions.ts` 里至少一个接口挂上它（`api-permissions.test`），后端 `@Endpoint` 照表（`route-permissions.test`）；
 * 3. 前端 `lib/permission-labels.ts` 两张表各补一行 + 两份语言文件（`permission-labels.test`）；
 * 4. 页面上的按钮 / 查询进 `lib/routes/registry.ts` 的 `gated`（`registry.test`）。
 * 只影响按钮、不影响页面进不进得去的权限（下载这类），路由守卫里不要写它，穷举测试不扫它。
 */
export const Permission = {
    // ---- 工地 ----
    /** 建工地（含地址搜索） */
    SITE_CREATE: 'site.create',
    /** 改工地设置、管成员 */
    SITE_MANAGE: 'site.manage',
    /** 不受 SiteMember 限制，能进所有工地 */
    SITE_VIEW_ALL: 'site.viewAll',
    // ---- 图纸 ----
    DRAWING_VIEW: 'drawing.view',
    /** 传图纸、加删图钉 */
    DRAWING_UPLOAD: 'drawing.upload',
    /** 下载图纸原文件 / 整个工地打包（CR-043） */
    DRAWING_EXPORT: 'drawing.export',
    // ---- 进度表 ----
    SCHEDULE_VIEW: 'schedule.view',
    SCHEDULE_UPLOAD: 'schedule.upload',
    SCHEDULE_EXPORT: 'schedule.export',
    // ---- 日报 ----
    DAILY_LOG_WRITE: 'dailyLog.write',
    /** 看本工地所有人的日报 */
    DAILY_LOG_VIEW: 'dailyLog.view',
    /** 把本工地一段时间的日报连同照片视频打包下载 */
    DAILY_LOG_EXPORT: 'dailyLog.export',
    // ---- 隐蔽工程 ----
    HIDDEN_WORK_WRITE: 'hiddenWork.write',
    HIDDEN_WORK_VIEW: 'hiddenWork.view',
    HIDDEN_WORK_EXPORT: 'hiddenWork.export',
    // ---- 采购 ----
    PURCHASE_WRITE: 'purchase.write',
    /** 看本工地所有人的采购、汇总 */
    PURCHASE_VIEW: 'purchase.view',
    PURCHASE_REVIEW: 'purchase.review',
    /** 下载采购表 + 小票（CR-043 之前导出挂在 purchase.view 上） */
    PURCHASE_EXPORT: 'purchase.export',
    // ---- 考勤 ----
    /** 自己打卡 */
    ATTENDANCE_PUNCH: 'attendance.punch',
    /** 看本工地考勤 */
    ATTENDANCE_VIEW: 'attendance.view',
    /** 复核、补录、强制下班 */
    ATTENDANCE_MANAGE: 'attendance.manage',
    /** 下载考勤表 */
    ATTENDANCE_EXPORT: 'attendance.export',
    // ---- 账号 ----
    /** 建账号、改资料、改角色、重置密码 */
    USER_MANAGE: 'user.manage',
    /**
     * 建 / 改 / 删**本单位**的角色（CR-073）。只能建「比自己小」的：权限是自己的子集、不带管人的两项。
     * 平台共享的角色（预置那几条）仍然只有超管能动 —— 「谁能发权限」那条底线没拆，只是把用户那套子集规则套到了角色上
     */
    ROLE_MANAGE: 'role.manage',
    // ---- 系统 ----
    AUDIT_VIEW: 'audit.view',
};
export const ALL_PERMISSIONS = Object.values(Permission);
const PERMISSION_SET = new Set(ALL_PERMISSIONS);
export function isPermissionKey(value) {
    return typeof value === 'string' && PERMISSION_SET.has(value);
}
export const PERMISSION_GROUPS = [
    { key: 'site', permissions: [Permission.SITE_CREATE, Permission.SITE_MANAGE, Permission.SITE_VIEW_ALL] },
    { key: 'drawing', permissions: [Permission.DRAWING_VIEW, Permission.DRAWING_UPLOAD, Permission.DRAWING_EXPORT] },
    { key: 'schedule', permissions: [Permission.SCHEDULE_VIEW, Permission.SCHEDULE_UPLOAD, Permission.SCHEDULE_EXPORT] },
    { key: 'dailyLog', permissions: [Permission.DAILY_LOG_WRITE, Permission.DAILY_LOG_VIEW, Permission.DAILY_LOG_EXPORT] },
    {
        key: 'hiddenWork',
        permissions: [Permission.HIDDEN_WORK_WRITE, Permission.HIDDEN_WORK_VIEW, Permission.HIDDEN_WORK_EXPORT],
    },
    {
        key: 'purchase',
        permissions: [
            Permission.PURCHASE_WRITE,
            Permission.PURCHASE_VIEW,
            Permission.PURCHASE_REVIEW,
            Permission.PURCHASE_EXPORT,
        ],
    },
    {
        key: 'attendance',
        permissions: [
            Permission.ATTENDANCE_PUNCH,
            Permission.ATTENDANCE_VIEW,
            Permission.ATTENDANCE_MANAGE,
            Permission.ATTENDANCE_EXPORT,
        ],
    },
    { key: 'user', permissions: [Permission.USER_MANAGE, Permission.ROLE_MANAGE] },
    { key: 'system', permissions: [Permission.AUDIT_VIEW] },
];
/**
 * 权限依赖（CR-040）：勾了左边，右边自动带上。
 *
 * 客户原话：「审批采购其实就等于管理者，自然需要查看这个site的全部人的采购」。
 * 只勾「审批采购」不勾「查看全工地采购」的角色，打开采购页全是 403 —— 权限等于没用。
 * 「复核打卡」没有「查看考勤」、「上传图纸」没有「查看图纸」同理。
 *
 * **反过来不成立**：「查看」可以单独勾。甲方、财务要看不要批，
 * 而 CR-039 的「只看的人看不到待处理事项」正是建立在这两个勾分开之上。
 *
 * 三处用它：角色页勾选时自动带上并锁住、RolesService 保存前补齐、
 * `hasPermission` 判断时兜底（库里万一有没补齐的行，行为也不会错）。
 */
export const PERMISSION_IMPLIES = {
    [Permission.PURCHASE_REVIEW]: [Permission.PURCHASE_VIEW],
    [Permission.ATTENDANCE_MANAGE]: [Permission.ATTENDANCE_VIEW],
    [Permission.DRAWING_UPLOAD]: [Permission.DRAWING_VIEW],
    [Permission.SCHEDULE_UPLOAD]: [Permission.SCHEDULE_VIEW],
    // 下载带上查看（CR-043）：下载的就是「本工地所有人的记录」，看不了就没什么可下的
    [Permission.DAILY_LOG_EXPORT]: [Permission.DAILY_LOG_VIEW],
    [Permission.HIDDEN_WORK_EXPORT]: [Permission.HIDDEN_WORK_VIEW],
    [Permission.PURCHASE_EXPORT]: [Permission.PURCHASE_VIEW],
    [Permission.ATTENDANCE_EXPORT]: [Permission.ATTENDANCE_VIEW],
    [Permission.DRAWING_EXPORT]: [Permission.DRAWING_VIEW],
    [Permission.SCHEDULE_EXPORT]: [Permission.SCHEDULE_VIEW],
};
/**
 * 下载权限（CR-043）。**只出现在页面里的按钮上，永远不进路由守卫 / 入口 / 落脚点** ——
 * 它们只多一个按钮，页面进不进得去由「查看」决定（下载自动带上查看）。
 * 两个穷举测试（概览、路由表）不扫它们，各自用静态断言锁住这条规矩。
 */
export const EXPORT_PERMISSIONS = [
    Permission.DAILY_LOG_EXPORT,
    Permission.HIDDEN_WORK_EXPORT,
    Permission.PURCHASE_EXPORT,
    Permission.ATTENDANCE_EXPORT,
    Permission.DRAWING_EXPORT,
    Permission.SCHEDULE_EXPORT,
];
/** 被谁带上：`purchase.view` ← `purchase.review` */
const IMPLIED_BY = (() => {
    const map = new Map();
    for (const [from, tos] of Object.entries(PERMISSION_IMPLIES)) {
        for (const to of tos)
            map.set(to, [...(map.get(to) ?? []), from]);
    }
    return map;
})();
/** 这个权限是被哪些已勾的权限带上的（角色页用来锁住勾选框、说明原因） */
export function impliedBy(key, granted) {
    return (IMPLIED_BY.get(key) ?? []).filter((from) => granted.includes(from));
}
/**
 * 补齐依赖、去重、按清单顺序排。清单里没有的 key 原样丢掉 ——
 * 校验是 RolesService 的事，这里只负责「保存下去的样子」。
 */
export function normalizePermissions(keys) {
    const set = new Set(keys);
    for (const key of keys) {
        for (const implied of PERMISSION_IMPLIES[key] ?? [])
            set.add(implied);
    }
    return ALL_PERMISSIONS.filter((key) => set.has(key));
}
/** 系统角色（超管）隐式拥有全部权限，不看 permissions 列。依赖在这里兜底 */
export function hasPermission(role, key) {
    if (!role)
        return false;
    if (role.isSystem)
        return true;
    if (role.permissions.includes(key))
        return true;
    return (IMPLIED_BY.get(key) ?? []).some((from) => role.permissions.includes(from));
}
export function hasAnyPermission(role, keys) {
    return keys.some((key) => hasPermission(role, key));
}
/**
 * 文件上传不是独立功能，任何会产生附件的权限都放行 `files/presign` / `files/commit`。
 */
export const UPLOAD_PERMISSIONS = [
    Permission.DAILY_LOG_WRITE,
    Permission.HIDDEN_WORK_WRITE,
    Permission.PURCHASE_WRITE,
    Permission.DRAWING_UPLOAD,
    Permission.SCHEDULE_UPLOAD,
];
/**
 * 能打开「工地概览」的权限（CR-039）：**恰好是能让概览里至少出现一格的那些**。
 *
 * 概览按字段过滤（见 overview.ts）：待复核打卡要复核权限、待审批采购要审批权限、
 * 在岗人数要考勤、今日日报要看全工地日报、近期隐蔽工程要看全工地隐蔽工程。
 * 这里多一个会出现「进得去、一格都没有」的空页（例如只看采购的人），
 * 少一个会有字段永远没人看得到。`overview.test.ts` 穷举锁着两者相等。
 */
export const OVERVIEW_PERMISSIONS = [
    Permission.DAILY_LOG_VIEW,
    Permission.PURCHASE_REVIEW,
    Permission.ATTENDANCE_VIEW,
    Permission.ATTENDANCE_MANAGE,
    Permission.HIDDEN_WORK_VIEW,
];
/** 「待处理事项」/ 手机「待办」：能审批采购或复核打卡（CR-039） */
export const REVIEW_PERMISSIONS = [
    Permission.PURCHASE_REVIEW,
    Permission.ATTENDANCE_MANAGE,
];
/**
 * 能拿本工地成员的「id + 姓名」下拉（`GET sites/:siteId/member-options`）。
 *
 * 看别人记录的页面要按人筛（隐蔽工程按拍摄人、补录打卡选工人），
 * 改之前它们调的是 `members`（要 site.manage，还带电话和时薪）——
 * 只有「查看隐蔽工程」的甲方打开页面就吃一个 403。
 */
export const MEMBER_OPTION_PERMISSIONS = [
    Permission.SITE_MANAGE,
    Permission.DAILY_LOG_VIEW,
    Permission.HIDDEN_WORK_VIEW,
    Permission.PURCHASE_VIEW,
    Permission.ATTENDANCE_VIEW,
];
/**
 * 管人的两项（CR-073）：带其中任一的角色就是「管理员」。
 * 管理员只有超管能造 —— 单位管理员建不出管理员，也建不出带这两项的角色。
 */
export const ADMIN_PERMISSIONS = [Permission.USER_MANAGE, Permission.ROLE_MANAGE];
/** 目标角色的权限是不是 actor 的子集。依赖算在内：目标勾了「审批采购」，等于也有「查看采购」，actor 两个都得有 */
function isSubsetOf(actor, target) {
    return normalizePermissions(target.permissions).every((key) => hasPermission(actor, key));
}
/**
 * 能不能管这个角色的人 / 把别人设成这个角色。
 *
 * 四条，缺一条都是洞：
 * 1. 系统角色（超管）谁都管不了，也不能赋给别人 —— 全系统唯一；
 * 2. 超管管所有非系统角色；
 * 3. 其他人要有 `user.manage`，而且**不能管带管人权限（`user.manage` / `role.manage`）的角色**
 *    —— 保住 CR-011「平级不能互管」的意图：两个账号管理员互相停用，谁先点谁赢；
 * 4. 目标角色的权限必须是自己权限的子集 —— 否则一个没有 `attendance.manage` 的
 *    账号管理员可以把带 `attendance.manage` 的角色发给别人（权限洗钱）。
 *
 * 单位（CR-073）不在这里判：人和角色归哪家由 `org-scope.ts` 管，service 两个都问。
 */
export function canManageRole(actor, target) {
    if (target.isSystem)
        return false;
    if (actor.isSystem)
        return true;
    if (!hasPermission(actor, Permission.USER_MANAGE))
        return false;
    if (hasAnyPermission(target, ADMIN_PERMISSIONS))
        return false;
    return isSubsetOf(actor, target);
}
/**
 * 这个角色能不能挂在归这家的人身上（CR-073）。建账号、改角色、把人挪到别家都问它：
 * - 单位自建的角色只能给同一家的人（共享角色给谁都行）—— 角色不决定人的归属；
 * - 管理员角色（带管人的两项）只能给归了某一家的人。平台上管人的只有超管，
 *   一个能管所有单位的人的平台账号就是第二个超管。
 */
export function canHoldRole(role, userOrgId) {
    if (!roleFitsUser(role.orgId, userOrgId))
        return false;
    return !(userOrgId === null && hasAnyPermission(role, ADMIN_PERMISSIONS));
}
/**
 * 能不能建 / 改 / 删这个角色（CR-073）。`target` 传改之后的样子（建角色时就是要建的那个）。
 *
 * 1. 系统角色谁都动不了；
 * 2. 超管什么都能动（共享的、任何一家的）；
 * 3. **共享角色只有超管能动** —— 它挂在所有单位的人身上，一家改了等于替所有家改了；
 * 4. 只能动自己范围里的（平台账号任何一家、单位账号只有自己家）；
 * 5. 要有 `role.manage`，目标不能带管人的两项，权限是自己的子集 —— 和 `canManageRole` 同一条子集规则。
 */
export function canEditRole(actor, target) {
    if (target.isSystem)
        return false;
    if (actor.role.isSystem)
        return true;
    if (target.orgId === null)
        return false;
    if (!inScope(scopeOf(actor), target.orgId))
        return false;
    if (!hasPermission(actor.role, Permission.ROLE_MANAGE))
        return false;
    if (hasAnyPermission(target, ADMIN_PERMISSIONS))
        return false;
    return isSubsetOf(actor.role, target);
}
/** 建账号 / 改角色下拉里能选的角色 */
export function assignableRoles(actor, roles) {
    return roles.filter((role) => canManageRole(actor, role));
}
const SUBCONTRACTOR_PERMISSIONS = [
    Permission.ATTENDANCE_PUNCH,
    Permission.DRAWING_VIEW,
    Permission.SCHEDULE_VIEW,
    Permission.DAILY_LOG_WRITE,
    Permission.HIDDEN_WORK_WRITE,
    Permission.HIDDEN_WORK_VIEW,
    Permission.PURCHASE_WRITE,
];
export const PRESET_ROLES = [
    {
        name: '超级管理员',
        description: '全部权限，全系统唯一。权限不可修改，角色不可删除。',
        isSystem: true,
        permissions: [],
    },
    {
        name: '直属工头',
        description: '和分包工头能做的事一样，另外能看到所有工地。',
        isSystem: false,
        permissions: [...SUBCONTRACTOR_PERMISSIONS, Permission.SITE_VIEW_ALL],
    },
    {
        name: '分包工头',
        description: '打卡、看图纸和进度表、写日报、拍隐蔽工程、记采购。只看被指派的工地。',
        isSystem: false,
        permissions: SUBCONTRACTOR_PERMISSIONS,
    },
    {
        name: '直属工人',
        description: '只有打卡，可以换工地。',
        isSystem: false,
        permissions: [Permission.ATTENDANCE_PUNCH],
    },
    {
        // CR-073：各家的管理员。平台共享，只有超管能把它赋给人（带 user.manage，canManageRole 挡着）。
        // 单公司部署（ORG_KIND=branch）时超管在角色页把名字改成「分公司管理员」—— 角色名是数据，不走 i18n（CR-038）
        name: '公司管理员',
        description: '本公司的全部权限：建账号、建角色、建工地、看审计。只看得到本公司的人和工地。',
        isSystem: false,
        permissions: ALL_PERMISSIONS,
    },
];
/** 超管那条预置角色的 id。迁移按 PRESET_ROLES 顺序插，它排第一 */
export const SYSTEM_ROLE_ID = 1;
//# sourceMappingURL=permissions.js.map